import { useState, useCallback } from 'react';
import { apiClient } from '../services/api.client';
import type { Client, CreateClientDto } from '@lnr-v2/types';

interface UseCreateClientReturn {
  createClient: (data: CreateClientDto) => Promise<Client>;
  loading: boolean;
  error: Error | null;
  reset: () => void;
}

export const useCreateClient = (): UseCreateClientReturn => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const createClient = useCallback(
    async (data: CreateClientDto): Promise<Client> => {
      setLoading(true);
      setError(null);

      try {
        const response = await apiClient.post('clients', {
          json: data,
        });

        const client = await response.json<Client>();
        return client;
      } catch (err) {
        let error: Error;

        if (err && typeof err === 'object' && 'response' in err) {
          try {
            const response = (err as { response: Response }).response;
            const errorData = await response.json();
            error = new Error(errorData.message || 'Failed to create client');
          } catch {
            error = new Error('Failed to create client');
          }
        } else if (err instanceof Error) {
          error = err;
        } else {
          error = new Error('Failed to create client');
        }

        setError(error);
        throw error;
      } finally {
        setLoading(false);
      }
    },
    []
  );

  const reset = useCallback(() => {
    setError(null);
  }, []);

  return {
    createClient,
    loading,
    error,
    reset,
  };
};
